(() => {
  if (customElements.get('ac-newsletter-popup')) return;

  const pendingKey = 'ac:newsletter:pending';
  const discountKey = 'ac:newsletter:discount';
  const storage = {
    read(area, key) {
      try { return JSON.parse(window[area].getItem(key) || 'null'); }
      catch { return null; }
    },
    write(area, key, value) {
      try { window[area].setItem(key, JSON.stringify(value)); }
      catch { /* The form and manual popup still work when storage is unavailable. */ }
    },
    remove(area, key) {
      try { window[area].removeItem(key); }
      catch { /* Storage is optional. */ }
    },
  };

  class AcNewsletterPopup extends HTMLElement {
    #events;
    #dialog;
    #timer = 0;
    #closeTimer = 0;
    #frame = 0;
    #autoFrame = 0;
    #generation = 0;
    #styles;
    #autoDue = false;
    #autoConsumed = false;
    #confirmed = false;
    #returnPending = false;
    #requests = new Map();
    #discount;
    #discountRequest;

    get #key() { return `ac:newsletter:${this.dataset.offer || 'newsletter'}`; }
    get #layerId() { return `newsletter:${this.dataset.sectionId}`; }

    connectedCallback() {
      this.#events?.abort();
      this.#events = new AbortController();
      this.#dialog = this.querySelector('[data-ac-newsletter-dialog]');
      if (!this.#dialog) return;
      const { signal } = this.#events;
      this.#confirmed = this.dataset.subscribed === 'true' || storage.read('localStorage', this.#key)?.subscribed === true;
      const discount = storage.read('sessionStorage', discountKey);
      this.#discount = discount?.code === this.dataset.offer?.trim() ? discount : null;
      if (this.#discount && this.#discount.state !== 'removed') this.#discount.state = 'applying';
      this.#syncView();
      this.#discountMessage();
      this.#syncTriggers();
      this.#dialog.addEventListener('click', this.#click, { signal });
      this.#dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
      this.#dialog.addEventListener('close', () => this.#finishClose(), { signal });
      // Install before interaction: Shopify captures this submit function when it
      // binds CAPTCHA, then calls it only after its verification has completed.
      for (const form of document.querySelectorAll('[data-ac-newsletter-form]')) {
        if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound) continue;
        form.submit = () => this.#send(form);
        form.dataset.acNewsletterAjax = 'true';
      }
      document.addEventListener('submit', this.#submit, { capture: true, signal });
      document.addEventListener('visibilitychange', this.#tryAutoOpen, { signal });
      document.addEventListener('ac:cookie:change', this.#tryAutoOpen, { signal });
      document.addEventListener('ac:layer:change', this.#tryAutoOpen, { signal });
      document.addEventListener('focusout', () => {
        window.cancelAnimationFrame(this.#autoFrame);
        this.#autoFrame = requestAnimationFrame(this.#tryAutoOpen);
      }, { signal });
      document.addEventListener('shopify:section:load', this.#syncTriggers, { signal });
      document.addEventListener('ac:cart-updated', (event) => {
        if (!event.detail?.refreshed) return;
        const cart = event.detail.cart;
        const match = cart?.discount_codes?.find(entry => entry.code.toLowerCase() === this.#discount?.code?.toLowerCase());
        if (match && this.#discount.state !== 'removed') this.#setDiscount(match.applicable ? 'applied' : 'inapplicable', cart);
        this.#applyDiscount(cart);
      }, { signal });
      document.addEventListener('ac:discount-code-removed', (event) => {
        if (event.detail?.code?.toLowerCase() !== this.#discount?.code?.toLowerCase()) return;
        window.AcCartActions?.cancelWelcomeDiscount();
        this.#setDiscount('removed');
      }, { signal });
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open();
      }, { signal });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.close();
      }, { signal });
      window.addEventListener('pageshow', this.#resetForms, { signal });
      if (document.readyState === 'complete') this.#loaded();
      else window.addEventListener('load', this.#loaded, { once: true, signal });
    }

    disconnectedCallback() {
      if (this.#discountRequest) window.AcCartActions?.cancelWelcomeDiscount();
      this.#generation++;
      this.#events?.abort();
      for (const [form, request] of this.#requests) {
        request.controller.abort();
        clearTimeout(request.timer);
        this.#finishRequest(form, request);
      }
      clearTimeout(this.#timer);
      clearTimeout(this.#closeTimer);
      cancelAnimationFrame(this.#frame);
      cancelAnimationFrame(this.#autoFrame);
      this.#dialog?.close();
      window.AcLayerManager?.release(this.#layerId, { restoreFocus: false });
      for (const trigger of document.querySelectorAll('[data-ac-footer-discount] .ac-ref-footer__discount-action')) {
        if (trigger.dataset.acNewsletterFallback) trigger.setAttribute('aria-controls', trigger.dataset.acNewsletterFallback);
        trigger.removeAttribute('aria-haspopup');
        trigger.removeAttribute('aria-expanded');
      }
    }

    /** @param {{trigger?: HTMLElement|null, automatic?: boolean}} options */
    async open({ trigger = null, automatic = false } = {}) {
      if (!this.isConnected) return false;
      // Returning subscribers can claim from the existing coupon view without signing up again.
      const code = this.dataset.offer?.trim();
      if (this.#confirmed && code && !this.#discount) {
        this.#discount = { code, state: 'waiting', signature: null };
        this.#applyDiscount();
      }
      const cookies = document.querySelector('ac-cookie-consent');
      if (automatic && cookies?.blocksNewsletter) { this.#autoConsumed = false; this.#autoDue = true; return false; }
      if (!automatic && cookies?.dataset.state !== 'closed' && cookies?.close) await cookies.close();
      if (this.#dialog.open) {
        if (this.#dialog.dataset.state === 'closing') {
          clearTimeout(this.#closeTimer);
          this.#dialog.dataset.state = 'open';
        }
        return true;
      }
      const generation = ++this.#generation;
      clearTimeout(this.#timer);
      this.#autoConsumed = true;
      try {
        await this.#loadStyles();
        if (!this.isConnected || generation !== this.#generation) return false;
        // Recheck after CSS loads: the visitor may have opened another layer meanwhile.
        if (window.AcLayerManager?.isLocked() || document.querySelector('dialog[open]')) {
          if (automatic) { this.#autoConsumed = false; this.#autoDue = true; }
          return false;
        }
        const opener = trigger || document.activeElement;
        for (const button of this.querySelectorAll('[data-ac-newsletter-copy]')) button.textContent = this.dataset.copyLabel;
        const copyFeedback = this.querySelector('[data-ac-newsletter-copy-feedback]');
        if (copyFeedback) { copyFeedback.hidden = true; copyFeedback.textContent = ''; }
        this.querySelector('[data-ac-newsletter-live]').textContent = '';
        this.#dialog.dataset.state = 'opening';
        this.#dialog.showModal();
        window.AcLayerManager?.acquire(this.#layerId, {
          opener,
          getFocusRoots: () => [this.#dialog],
          getInertRoots: () => [...document.body.children].filter((root) => !root.contains(this) && !root.matches('script, style, link')),
          onEscape: () => this.close(),
        });
        this.#syncTriggers();
        this.#frame = requestAnimationFrame(() => {
          this.#frame = requestAnimationFrame(() => {
            if (generation !== this.#generation || !this.#dialog.open) return;
            this.#dialog.dataset.state = 'open';
            // The title avoids raising the mobile keyboard during automatic opening.
            this.querySelector('[data-ac-newsletter-title]')?.focus({ preventScroll: true });
          });
        });
        return true;
      } catch {
        this.#autoConsumed = true;
        document.querySelector('ac-overlay-system')?.announce(this.dataset.openError);
        return false;
      }
    }

    close() {
      this.#generation++;
      cancelAnimationFrame(this.#frame);
      if (!this.#dialog?.open || this.#dialog.dataset.state === 'closing') return;
      this.#autoConsumed = true;
      if (!window.Shopify?.designMode && !this.#confirmed) {
        storage.write('localStorage', this.#key, { dismissedUntil: Date.now() + Number(this.dataset.dismissDays || 7) * 86400000 });
      }
      this.#dialog.dataset.state = 'closing';
      this.#closeTimer = setTimeout(() => {
        this.#dialog.close();
        this.#finishClose();
      }, this.#duration());
    }

    #finishClose() {
      if (this.#dialog.open) return;
      clearTimeout(this.#closeTimer);
      this.#dialog.dataset.state = 'closed';
      this.#syncTriggers();
      window.AcLottieManager?.destroyWithin(this.#dialog);
      window.AcLayerManager?.release(this.#layerId);
    }

    #loadStyles() {
      if (this.#styles) return this.#styles;
      this.#styles = new Promise((resolve, reject) => {
        const existing = document.querySelector('link[data-ac-newsletter-styles]');
        if (existing?.sheet) { resolve(); return; }
        const link = existing || document.createElement('link');
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', () => { link.remove(); this.#styles = null; reject(new Error('Newsletter stylesheet unavailable')); }, { once: true });
        if (existing) return;
        link.rel = 'stylesheet';
        link.href = this.dataset.cssUrl;
        link.dataset.acNewsletterStyles = '';
        document.head.append(link);
      });
      return this.#styles;
    }

    #loaded = () => {
      if (!this.isConnected) return;
      this.#restoreReturn();
      if (['waiting', 'applying'].includes(this.#discount?.state)) this.#applyDiscount();
      if (window.Shopify?.designMode || this.#returnPending || this.dataset.autoOpen !== 'true' || this.#suppressed()) return;
      if (['cart', 'password', 'customers/login', 'customers/register'].includes(this.dataset.pageType)) return;
      this.#timer = setTimeout(() => { this.#autoDue = true; this.#tryAutoOpen(); }, Number(this.dataset.delay || 8) * 1000);
    };

    #suppressed() {
      const receipt = storage.read('localStorage', this.#key);
      return this.#confirmed || receipt?.subscribed === true || Number(receipt?.dismissedUntil || 0) > Date.now();
    }

    #tryAutoOpen = () => {
      if (!this.#autoDue || this.#autoConsumed || this.#suppressed() || document.hidden) return;
      if (document.querySelector('ac-cookie-consent')?.blocksNewsletter) return;
      if (window.AcLayerManager?.isLocked() || document.querySelector('dialog[open]')) return;
      if (document.activeElement?.closest('form, [contenteditable="true"]')) return;
      this.open({ automatic: true });
    };

    #submit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-ac-newsletter-form]')) return;
      if (!form.checkValidity()) return;
      if (form.dataset.acNewsletterAjax === 'true') {
        event.preventDefault();
        // The supported protect callback starts Shopify's verified submit method.
        // Avoid a second submission from the native submit listeners.
        event.stopImmediatePropagation();
        if (this.#requests.has(form)) return;
        const request = { controller: new AbortController(), sent: false, timer: 0 };
        this.#requests.set(form, request);
        this.#setBusy(form, true);
        this.#message(form, this.dataset.submitting);
        request.timer = setTimeout(() => {
          request.controller.abort();
          this.#message(form, this.dataset.submitTimeout, true);
          this.#finishRequest(form, request);
        }, 45000);
        if (window.Shopify?.captcha?.protect) {
          try { window.Shopify.captcha.protect(form, () => {
            if (this.#requests.get(form) === request) form.submit();
          }); }
          catch { this.#message(form, this.dataset.submitError, true); this.#finishRequest(form, request); }
        } else this.#send(form);
        return;
      }
      // Preserve native recovery if the form was already bound before upgrade.
      storage.write('sessionStorage', pendingKey, { id: form.id, source: form.dataset.acNewsletterForm, path: location.pathname, y: window.scrollY, time: Date.now() });
      this.#autoConsumed = true;
      clearTimeout(this.#timer);
      const returnTo = form.querySelector('[name="return_to"]');
      if (returnTo) {
        const url = new URL(location.href);
        url.searchParams.set('customer_posted', 'true');
        url.hash = '';
        returnTo.value = url.pathname + url.search;
      }
      if (event.defaultPrevented) return;
      this.#setBusy(form, true);
      this.#message(form, this.dataset.submitting);
      // A blocked native navigation must not leave a permanently disabled form.
      this.#timer = setTimeout(this.#resetForms, 15000);
    };

    #resetForms = (event) => {
      if (event?.persisted) {
        this.#confirmed = this.dataset.subscribed === 'true' || storage.read('localStorage', this.#key)?.subscribed === true;
        const discount = storage.read('sessionStorage', discountKey);
        this.#discount = discount?.code === this.dataset.offer?.trim() ? discount : null;
        this.#syncView();
        this.#discountMessage();
        this.#applyDiscount();
      }
      for (const form of document.querySelectorAll('[data-ac-newsletter-form][aria-busy]')) {
        if (!this.#requests.has(form)) this.#setBusy(form, false);
      }
    };

    #setBusy(form, busy) {
      form.toggleAttribute('aria-busy', busy);
      if (busy) form.setAttribute('aria-busy', 'true');
      const button = form.querySelector('[type="submit"]');
      if (!button) return;
      button.disabled = busy;
      const label = button.querySelector('.ac-action__label') || button;
      button.dataset.acSubmitLabel ||= label.textContent;
      label.textContent = busy ? this.dataset.submittingLabel : button.dataset.acSubmitLabel;
    }

    #message(form, text, error = false) {
      const message = form.querySelector('[data-ac-newsletter-feedback]');
      if (!message) return;
      message.hidden = !text;
      message.dataset.error = String(error);
      message.textContent = text;
    }

    #finishRequest(form, request) {
      clearTimeout(request.timer);
      if (this.#requests.get(form) !== request) return;
      this.#requests.delete(form);
      this.#setBusy(form, false);
    }

    async #send(form) {
      const request = this.#requests.get(form);
      if (!request || request.sent) return;
      request.sent = true;
      try {
        const data = new FormData(form);
        const returnTo = new URL(location.href);
        returnTo.hash = '';
        // Shopify redirects here only after accepting the native customer form.
        // Render only this section; do not download or execute another full page.
        returnTo.searchParams.set('customer_posted', 'true');
        returnTo.searchParams.set('section_id', 'ac-newsletter-popup');
        data.set('return_to', returnTo.pathname + returnTo.search);
        const response = await fetch(form.action, {
          method: 'POST', body: data, credentials: 'same-origin',
          headers: { Accept: 'text/html' }, signal: request.controller.signal,
        });
        if (this.#requests.get(form) !== request || !this.isConnected) return;
        const resultUrl = new URL(response.url);
        if (resultUrl.origin === location.origin && /\/challenge(?:\/|$)/.test(resultUrl.pathname)) {
          this.#message(form, this.dataset.challenge);
          location.assign(response.url);
          return;
        }
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (this.#requests.get(form) !== request || !this.isConnected) return;
        const returnedForm = html.getElementById(form.id) || html.querySelector('[data-ac-newsletter-form="popup"]');
        const result = returnedForm?.querySelector('[data-ac-newsletter-result]')?.dataset.acNewsletterResult;
        if (response.ok && result === 'success') {
          this.#confirm();
          this.#message(form, this.dataset.successFeedback);
          storage.remove('sessionStorage', pendingKey);
          if (!this.#dialog.open) await this.open();
          this.querySelector('[data-ac-newsletter-title]')?.focus({ preventScroll: true });
        } else {
          const error = returnedForm?.querySelector('[data-ac-newsletter-server-error]')?.textContent.trim();
          this.#message(form, error || this.dataset.submitError, true);
        }
      } catch (error) {
        if (this.#requests.get(form) === request) this.#message(form, error.name === 'AbortError' ? this.dataset.submitTimeout : this.dataset.submitError, true);
      } finally { this.#finishRequest(form, request); }
    }

    #confirm() {
      this.#confirmed = true;
      this.#autoConsumed = true;
      clearTimeout(this.#timer);
      if (!window.Shopify?.designMode) storage.write('localStorage', this.#key, { subscribed: true });
      this.#syncView();
      const code = this.dataset.offer?.trim();
      if (code && !this.#discount) this.#discount = { code, state: 'waiting', signature: null };
      this.#applyDiscount();
    }

    #cartSignature(cart) {
      return JSON.stringify([cart.currency, (cart.items || []).map(item => [item.variant_id, item.quantity])]);
    }

    #setDiscount(state, cart) {
      if (!this.#discount) return;
      this.#discount.state = state;
      if (cart) this.#discount.signature = this.#cartSignature(cart);
      storage.write('sessionStorage', discountKey, this.#discount);
      this.#discountMessage();
    }

    #discountMessage() {
      const message = this.querySelector('[data-ac-newsletter-auto-status]');
      if (!message) return;
      const labels = { applying: this.dataset.discountApplying, waiting: this.dataset.discountWaiting,
        applied: this.dataset.discountApplied, inapplicable: this.dataset.discountInapplicable,
        error: this.dataset.discountManual, removed: this.dataset.discountManual };
      const text = this.#confirmed ? labels[this.#discount?.state] || '' : '';
      message.textContent = text;
      message.hidden = !text;
    }

    #applyDiscount(cart) {
      if (!this.#confirmed || !this.#discount || this.#discount.state === 'removed'
        || !window.AcCartActions || window.Shopify?.designMode || this.#discountRequest) return;
      if (cart && this.#discount.signature === this.#cartSignature(cart)) return;
      if (cart && !cart.item_count) { this.#setDiscount('waiting', cart); return; }
      this.#setDiscount('applying');
      this.#discountRequest = (async () => {
        try {
          const result = await window.AcCartActions.applyWelcomeDiscount(this.#discount.code);
          if (!this.isConnected || this.#discount.state === 'removed') return;
          const match = result.discount_codes?.find(entry => entry.code.toLowerCase() === this.#discount.code.toLowerCase());
          this.#setDiscount(!result.item_count ? 'waiting' : match?.applicable === true ? 'applied' : 'inapplicable', result);
          if (result.item_count) await window.AcCartActions.sync();
        } catch {
          if (this.isConnected && this.#discount.state !== 'removed') this.#setDiscount('error', cart);
        } finally { this.#discountRequest = null; }
      })();
    }

    #restoreReturn() {
      const record = storage.read('sessionStorage', pendingKey);
      const pending = record && record.path === location.pathname && Date.now() - record.time < 1800000 ? record : null;
      if (record && !pending) storage.remove('sessionStorage', pendingKey);
      const form = pending ? document.getElementById(pending.id) : this.querySelector('[data-ac-newsletter-form]');
      const result = form?.querySelector('[data-ac-newsletter-result]')?.dataset.acNewsletterResult;
      if (!result) return;
      storage.remove('sessionStorage', pendingKey);
      this.#returnPending = true;
      this.#autoConsumed = true;
      if (result === 'success') {
        this.#confirm();
      }
      if (pending && Number.isFinite(pending.y)) window.scrollTo({ top: pending.y, behavior: 'instant' });
      // Footer validation stays beside the footer field; popup validation reopens the dialog.
      if (result === 'success' || pending?.source !== 'footer') this.open();
    }

    #syncView() {
      const signup = this.querySelector('[data-ac-newsletter-signup]');
      const success = this.querySelector('[data-ac-newsletter-success]');
      if (signup) signup.hidden = this.#confirmed;
      if (success) success.hidden = !this.#confirmed;
      const title = this.querySelector('[data-ac-newsletter-title]');
      if (this.#confirmed && title) title.textContent = this.dataset.successHeading;
    }

    #syncTriggers = () => {
      for (const trigger of document.querySelectorAll('[data-ac-footer-discount] .ac-ref-footer__discount-action')) {
        if (!trigger.dataset.acNewsletterFallback) trigger.dataset.acNewsletterFallback = trigger.getAttribute('aria-controls') || '';
        trigger.setAttribute('aria-controls', this.#dialog.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', String(this.#dialog.open));
      }
    };

    #click = (event) => {
      if (event.target.closest('[data-ac-newsletter-dismiss]')) { this.close(); return; }
      const button = event.target.closest('[data-ac-newsletter-copy]');
      if (button) this.#copy(button);
    };

    async #copy(button) {
      const input = document.getElementById(button.getAttribute('aria-controls'));
      if (!(input instanceof HTMLInputElement)) return;
      const feedback = this.querySelector('[data-ac-newsletter-copy-feedback]');
      button.disabled = true;
      button.textContent = this.dataset.copyLabel;
      try {
        await navigator.clipboard.writeText(input.value);
        if (!this.isConnected) return;
        button.textContent = this.dataset.copiedLabel;
        if (feedback) { feedback.hidden = false; feedback.textContent = this.dataset.copySuccess; }
        this.querySelector('[data-ac-newsletter-live]').textContent = this.dataset.copiedLabel;
      } catch {
        input.focus({ preventScroll: true });
        input.select();
        if (feedback) { feedback.hidden = false; feedback.textContent = this.dataset.copyHelp; }
        this.querySelector('[data-ac-newsletter-live]').textContent = this.dataset.copyHelp;
      } finally { button.disabled = false; }
    }

    #duration() {
      if (document.body.dataset.acMotion === 'reduced' || matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
      const value = getComputedStyle(this.#dialog).getPropertyValue('--ac-newsletter-duration').trim();
      return value.endsWith('ms') ? Number.parseFloat(value) || 0 : (Number.parseFloat(value) || 0) * 1000;
    }
  }

  customElements.define('ac-newsletter-popup', AcNewsletterPopup);
})();
